import React from 'react';
import { RelatedContent } from '../RelatedContent';
import { FeaturedPosts } from '../FeaturedPosts';
import {
  ILearningCenterArticle,
  ILearningCenterNewPopularArticlesFields,
  ILearningCenterRelatedArticlesFields,
  ILearningCenterSidebarCalloutFields,
  IVariationContainerFields
} from '../../../../../types/contentful';
import { StickySidebarWrapper } from './styles';
import { SidebarCallout } from 'src/components/LearningCenter/Components/SidebarCallout';
import { VariationContainer } from '../../../VariationContainer';
import { usePageDataContext } from '~/context/PageData';

interface StickySidebarProps {
  blocks: ILearningCenterArticle['fields']['sidebar'];
  excludeSidebarCallout?: boolean;
}

const top = 96;
const blocksMap = {
  learningCenterNewPopularArticles: (
    fields: ILearningCenterNewPopularArticlesFields,
    sysId: string
  ) => <FeaturedPosts key={sysId} {...fields} />,
  learningCenterRelatedArticles: (
    fields: ILearningCenterRelatedArticlesFields,
    sysId: string
  ) => <RelatedContent key={sysId} {...fields} />,
  learningCenterSidebarCallout: (
    fields: ILearningCenterSidebarCalloutFields,
    sysId: string
  ) => <SidebarCallout key={sysId} {...fields} />,
  variationContainer: (fields: IVariationContainerFields, sysId: string) => (
    <VariationContainer key={sysId} sysId={sysId} {...fields} />
  )
};

export function StickySidebar({
  blocks,
  excludeSidebarCallout
}: StickySidebarProps) {
  const {
    articleConfig: {
      globalRelatedContent,
      globalNewsletterSignupSidebar: globalCTA
    } = {}
  } = usePageDataContext();

  const globalNewsletterSignupSidebar = !excludeSidebarCallout
    ? globalCTA
    : null;

  return (
    <StickySidebarWrapper top={top} data-testid="article-sidebar">
      {blocks?.map(({ sys, fields }) => {
        const blockId = sys.contentType?.sys.id as keyof typeof blocksMap;

        if (
          blockId === 'learningCenterSidebarCallout' &&
          globalNewsletterSignupSidebar &&
          !excludeSidebarCallout
        ) {
          if (
            globalNewsletterSignupSidebar.sys.contentType.sys.id ===
            'variationContainer'
          ) {
            return (
              <VariationContainer
                key="global-newsletter-signup-sidebar"
                sysId={globalNewsletterSignupSidebar.sys.id}
                {...globalNewsletterSignupSidebar.fields}
              />
            );
          }
          return (
            <SidebarCallout
              key="global-newsletter-signup-sidebar"
              {...globalNewsletterSignupSidebar.fields}
            />
          );
        }

        // Render the block if it exists
        if (blockId && blocksMap[blockId]) {
          return blocksMap[blockId](fields, sys.id);
        }

        return null;
      })}

      {/* Fallback for RelatedContent */}
      {!blocks?.some(
        ({ sys }) => sys.contentType?.sys.id === 'learningCenterRelatedArticles'
      ) &&
        globalRelatedContent &&
        (globalRelatedContent.sys.contentType.sys.id ===
        'variationContainer' ? (
          <VariationContainer
            key="global-related-content"
            sysId={globalRelatedContent.sys.id}
            {...globalRelatedContent.fields}
          />
        ) : (
          <RelatedContent
            key="global-related-content"
            {...globalRelatedContent.fields}
          />
        ))}

      {/* Fallback for SidebarCallout */}
      {!blocks?.some(
        ({ sys }) => sys.contentType?.sys.id === 'learningCenterSidebarCallout'
      ) &&
        globalNewsletterSignupSidebar &&
        (globalNewsletterSignupSidebar.sys.contentType.sys.id ===
        'variationContainer' ? (
          <VariationContainer
            key="global-newsletter-signup-sidebar"
            sysId={globalNewsletterSignupSidebar.sys.id}
            {...globalNewsletterSignupSidebar.fields}
          />
        ) : (
          <SidebarCallout
            key="global-newsletter-signup-sidebar"
            {...globalNewsletterSignupSidebar.fields}
          />
        ))}
    </StickySidebarWrapper>
  );
}
